/**
 * Vivi Store: keep the variant picker on a combination that actually exists.
 *
 * Every garment is a one-off: a colour usually exists in a single size, so most
 * colour + size combinations have no variant at all.
 *
 * Horizon already renders everything needed to detect that, on each option value:
 *   - `data-variant-id`      the variant that results from combining this value with the
 *                            other currently selected values (absent = no such variant).
 *   - `data-option-available` whether that value still has a purchasable combination.
 * It also blanks the form's hidden `id` input and disables "Add to cart" when the selected
 * combination has no variant, but it leaves the buyer stranded on that dead combination.
 *
 * This module keeps the value the buyer just chose and moves the *other* options to a value
 * that exists for it, so the form always points at the variant the picker is showing.
 * It works off option positions, never option names, so it does not care what the size
 * option is called.
 */

/** Option position (1-based) the buyer last changed, per picker. @type {WeakMap<Element, string>} */
const lastChangedPosition = new WeakMap();

/** Consecutive automatic corrections, per picker, to bound the re-render loop. @type {WeakMap<Element, number>} */
const correctionDepth = new WeakMap();

const MAX_CORRECTIONS = 4;

/** True while this module is the one changing a value, so it is not mistaken for a buyer choice. */
let applyingCorrection = false;

/**
 * @param {Element} control
 * @returns {string} The 1-based position of the option the control belongs to, '' if unknown.
 */
const positionOf = (control) => (/** @type {HTMLElement} */ (control).dataset.inputId ?? '').split('-')[0] ?? '';

/**
 * @param {Element} control
 * @returns {boolean}
 */
const isSelected = (control) =>
  control instanceof HTMLOptionElement
    ? control.selected
    : /** @type {HTMLInputElement} */ (control).checked;

/**
 * Whether this value, combined with the other selected values, maps to a real variant.
 * @param {Element} control
 * @returns {boolean}
 */
const hasVariant = (control) => Boolean(/** @type {HTMLElement} */ (control).dataset.variantId);

/**
 * Horizon only marks this on button/swatch inputs; when absent there is no reason to avoid it.
 * @param {Element} control
 * @returns {boolean}
 */
const isPurchasable = (control) => /** @type {HTMLElement} */ (control).dataset.optionAvailable !== 'false';

/**
 * @param {Element} picker
 * @returns {Element[]}
 */
const controlsOf = (picker) =>
  Array.from(picker.querySelectorAll('fieldset input[type="radio"], select option')).filter((control) =>
    Boolean(positionOf(control))
  );

/**
 * Selects a value the same way a buyer would, so the picker's own change handler re-renders.
 * @param {Element} control
 */
function choose(control) {
  applyingCorrection = true;

  try {
    if (control instanceof HTMLOptionElement) {
      const select = control.closest('select');
      if (!select) return;
      select.value = control.value;
      select.dispatchEvent(new Event('change', { bubbles: true }));
      return;
    }

    /** @type {HTMLInputElement} */ (control).click();
  } finally {
    applyingCorrection = false;
  }
}

/**
 * Moves every option other than the one the buyer just changed onto a value that exists.
 * @param {Element} picker
 */
function correctSelection(picker) {
  if (!picker.isConnected) return;

  const changedPosition = lastChangedPosition.get(picker);
  if (!changedPosition) return;

  const controls = controlsOf(picker);
  const selected = controls.filter(isSelected);
  if (!selected.length) return;

  // Every selected value resolves to a variant, so the combination exists. Nothing to fix.
  if (selected.every(hasVariant)) {
    correctionDepth.set(picker, 0);
    return;
  }

  const depth = correctionDepth.get(picker) ?? 0;
  if (depth >= MAX_CORRECTIONS) return;

  /** @type {Map<string, Element[]>} */
  const groups = new Map();

  for (const control of controls) {
    const position = positionOf(control);
    if (position === changedPosition) continue;
    const group = groups.get(position) ?? [];
    group.push(control);
    groups.set(position, group);
  }

  for (const group of groups.values()) {
    const current = group.find(isSelected);
    if (current && hasVariant(current)) continue;

    const replacement = group.find((control) => hasVariant(control) && isPurchasable(control)) ?? group.find(hasVariant);
    if (!replacement || replacement === current) continue;

    // One option at a time: selecting it re-renders the section, which re-runs this check.
    correctionDepth.set(picker, depth + 1);
    choose(replacement);
    return;
  }
}

// Remember which option the buyer touched, so a correction never overrides their own choice.
document.addEventListener(
  'change',
  (event) => {
    if (applyingCorrection) return;

    const target = event.target;
    if (!(target instanceof HTMLElement)) return;

    const picker = target.closest('variant-picker');
    if (!picker) return;

    const control = target instanceof HTMLSelectElement ? target.selectedOptions[0] : target;
    const position = control ? positionOf(control) : '';
    if (!position) return;

    lastChangedPosition.set(picker, position);
    correctionDepth.set(picker, 0);
  },
  true
);

customElements.whenDefined('variant-picker').then(() => {
  const prototype = customElements.get('variant-picker')?.prototype;

  if (!prototype || typeof prototype.updateVariantPicker !== 'function') {
    console.warn('[vivi] variant-picker.updateVariantPicker is missing; option auto-correction is disabled');
    return;
  }

  const updateVariantPicker = prototype.updateVariantPicker;

  // Runs right after the freshly rendered option availability has been morphed in.
  prototype.updateVariantPicker = function (newHtml) {
    const result = updateVariantPicker.call(this, newHtml);
    correctSelection(this);
    return result;
  };
});
